import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  QueryList,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import {NgFor, NgIf} from '@angular/common';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';
import {IconButtonComponent, ToolbarModule} from '@q9elements/ui-kit/components';
import {ModeDataService} from '../../../editor-state/mode-data.service';
import {DiagramTypeConfigService} from '../../../core/services/diagram-type-config.service';
import {UndoService} from '../../../commands/undo.service';
import {GraphHelperService} from '../../../diagram-render/helpers/graph-helper.service';
import {ActivityCommands} from '../../../commands/activity/activity.commands';
import {FlowlineCommands} from '../../../commands/flowline/flowline.commands';
import {StylesService} from '../../../diagram-render/styles/styles.service';
import {DragHandlerService} from '../shared/drag-handler.service';
import {MatButtonModule} from '@angular/material/button';
import {ActivityType} from 'src/app/diagram-render/styles/diagram-type-styles.model';
import {MAPS_TYPES_ENUM} from '../../shared/models/map.model';

@Component({
  selector: 'app-activity-toolbar',
  templateUrl: './activity-toolbar.component.html',
  styleUrls: ['./activity-toolbar.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    IconButtonComponent,
    ToolbarModule,
    TranslateModule,
    MatTooltipModule,
    MatButtonModule
  ]
})
export class ActivityToolbarComponent implements AfterViewInit {
  public readonly activityTypes = this.diagramTypeConfigSvc.activityTypes;
  public readonly showThreeElementsInRow = computed(() => {
    return MAPS_TYPES_ENUM.INTERACTION_FLOW === this.diagramTypeConfigSvc.currentType();
  });

  @ViewChildren('activityDragElement', {read: ElementRef})
  private activityDragElements: QueryList<ElementRef>;

  constructor(
    private readonly graphService: GraphHelperService,
    private modeDataSvc: ModeDataService,
    private readonly activityCommands: ActivityCommands,
    private readonly flowlineCommands: FlowlineCommands,
    private diagramTypeConfigSvc: DiagramTypeConfigService,
    private readonly undoService: UndoService,
    private stylesSvc: StylesService,
    private dragHandlerSvc: DragHandlerService
  ) {}

  ngAfterViewInit() {
    this.setupActivityDragHandlers();
  }

  public get gridTemplateColumns(): string {
    const rowCount = this.showThreeElementsInRow() ? 3 : Math.min(this.activityTypes.length, 5);

    return `repeat(${rowCount}, 1fr)`;
  }

  private setupActivityDragHandlers() {
    this.activityDragElements.forEach((elementRef, i) => {
      const activityType = this.activityTypes[i];
      const {w: width, h: height} = activityType.settings().defaultActivityShape;
      const previewElement = this.dragHandlerSvc.createPreviewElement(width, height);

      this.dragHandlerSvc.createDragHandler(
        (x, y) => this.createCommand(x, y, activityType),
        elementRef.nativeElement,
        previewElement
      );
    });
  }

  private createCommand(x: number, y: number, activityType: ActivityType) {
    const primaryStyle = this.stylesSvc.getPrimary('activity');
    const {infoStyle, ...restStyle} = primaryStyle;
    const freeNumber = this.graphService.getFreeNumber(2);
    const {
      dragHandlePosition: position,
      footerText,
      defaultResources,
      defaultAttributes,
      attributePadding,
      text,
      customStyle,
      style,
      createActivityWithFlowline
    } = activityType.settings(x, y);

    const activityData = {
      number: freeNumber,
      text,
      position,
      attributePadding,
      ...(footerText && {footerText}),
      ...(defaultAttributes && {attributes: defaultAttributes}),
      ...(defaultResources && {resources: defaultResources})
    };

    if (customStyle) {
      activityData['customStyle'] = customStyle;
      activityData['style'] = {
        ...restStyle,
        resourcesStyle: this.stylesSvc.getPrimary('resource'),
        ...style
      };
    }

    let flowlineCommand;

    if (createActivityWithFlowline) {
      return this.activityCommands
        .create(activityData)
        .chain((activityJson, activityCell) => {
          flowlineCommand = this.flowlineCommands.create(
            this.diagramTypeConfigSvc.flowlineSettings(
              activityJson.position.x,
              activityJson.position.y,
              activityCell.id
            ).config
          );
        })
        .chainAfter(() => {
          this.undoService.executeCommand(flowlineCommand);
        });
    }

    return this.activityCommands.create(activityData);
  }
}
